/**
 * Analysis dimensions, for every module.
 *
 * The chart of accounts says *what* a cost was; a dimension says *whose* it was. Any document that
 * ends up in the ledger should be able to carry them, so the pieces live here rather than inside
 * accounting — a module that adds a document adds `<DimensionFields>` and is done.
 */
import { put, useAction, useApi } from "@keel/core";
import { useState } from "react";
import { Select } from "./Select";
import { Field, Icon, Skeleton } from "./ui";

export interface DimensionValue {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}
export interface Dimension {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  position: number;
  values: DimensionValue[];
}

/** Dimension code → value code: the shape a document, a posting and a filter all use. */
export type DimensionMap = Record<string, string>;

/** The axes a posting can be tagged along. Only the ones in use take new postings. */
export function useDimensions(): Dimension[] {
  return (useApi<Dimension[]>("/accounting/dimensions").data ?? []).filter((d) => d.isActive);
}

/** `{COSTCENTRE: "RIGA"}` → `COSTCENTRE:RIGA,…`, the `?dim=` the reports take. */
export function dimQuery(value: DimensionMap): string {
  const parts = Object.entries(value).filter(([, v]) => v);
  return parts.length ? `&dim=${encodeURIComponent(parts.map(([k, v]) => `${k}:${v}`).join(","))}` : "";
}

/** One dropdown per axis — on a journal line, a document header, or a report toolbar. */
export function DimensionPicker({
  dims,
  value,
  onChange,
  anyLabel = "—",
}: {
  dims: Dimension[];
  value: DimensionMap;
  onChange: (next: DimensionMap) => void;
  anyLabel?: string;
}) {
  return (
    <>
      {dims.map((d) => (
        <Select
          key={d.id}
          value={value[d.code] ?? ""}
          aria-label={d.name}
          placeholder={anyLabel}
          onChange={(e) => {
            const next = { ...value };
            if (e.target.value) next[d.code] = e.target.value;
            else delete next[d.code];
            onChange(next);
          }}
        >
          <option value="">{anyLabel}</option>
          {d.values
            .filter((v) => v.isActive || value[d.code] === v.code)
            .map((v) => (
              <option key={v.id} value={v.code}>
                {v.code} · {v.name}
              </option>
            ))}
        </Select>
      ))}
    </>
  );
}

/**
 * The labelled form fields for a document's dimensions. This is what a module's editor uses, so
 * every document asks the question the same way.
 *
 * Renders nothing when no dimensions are defined: a company that does not analyse should not be
 * shown an empty box asking it to.
 */
export function DimensionFields({
  value,
  onChange,
  hint = "Carried onto every posting this document makes",
}: {
  value: DimensionMap;
  onChange: (next: DimensionMap) => void;
  hint?: string;
}) {
  const dims = useDimensions();
  if (dims.length === 0) return null;
  return (
    <>
      {dims.map((d, i) => (
        <Field key={d.id} label={d.name} hint={i === 0 ? hint : undefined}>
          <Select
            value={value[d.code] ?? ""}
            placeholder="—"
            onChange={(e) => {
              const next = { ...value };
              if (e.target.value) next[d.code] = e.target.value;
              else delete next[d.code];
              onChange(next);
            }}
          >
            <option value="">—</option>
            {d.values
              .filter((v) => v.isActive || value[d.code] === v.code)
              .map((v) => (
                <option key={v.id} value={v.code}>
                  {v.code} · {v.name}
                </option>
              ))}
          </Select>
        </Field>
      ))}
    </>
  );
}

/** The tags a posted line carries, small enough to sit in a ledger row. */
export function DimensionTags({ value }: { value?: DimensionMap | null }) {
  const parts = Object.entries(value ?? {});
  if (!parts.length) return null;
  return (
    <span className="chips" style={{ display: "inline-flex", gap: 4, marginLeft: 6 }}>
      {parts.map(([k, v]) => (
        <span key={k} className="perm-tag" title={k}>
          {v}
        </span>
      ))}
    </span>
  );
}

interface DefaultRow {
  id: string;
  dimensionCode: string;
  valueCode: string;
  rule: string;
}

const RULES: [string, string, string][] = [
  ["optional", "Suggest it", "Filled in on new documents; anybody can change or clear it."],
  ["mandatory", "Require it", "A document for this record has to end up carrying this axis."],
  ["same_code", "Always this value", "The document is overruled — useful when it is simply a fact."],
];

/**
 * The default dimensions set on one record — a customer, a product, an account, an employee.
 *
 * Drop it into any card: `<DimensionDefaults entityType="customer" entityId={party.id} />`. The
 * point of the whole feature is that the answer gets written down once, here, on the thing that
 * knows it, instead of being typed onto every document by somebody trying to remember.
 */
export function DimensionDefaults({
  entityType,
  entityId,
  canWrite,
}: {
  entityType: string;
  entityId: string;
  canWrite?: boolean;
}) {
  const dims = useDimensions();
  const path = `/accounting/dimension-defaults/${entityType}/${entityId}`;
  const { data } = useApi<DefaultRow[]>(entityId ? path : null);
  const { run, busy } = useAction();
  const [draft, setDraft] = useState<Record<string, { value: string; rule: string }> | null>(null);

  if (dims.length === 0) return null;
  if (!data) return <Skeleton rows={2} />;

  const current =
    draft ??
    Object.fromEntries(
      dims.map((d) => {
        const row = data.find((r) => r.dimensionCode === d.code);
        return [d.code, { value: row?.valueCode ?? "", rule: row?.rule ?? "optional" }];
      }),
    );
  const dirty = draft !== null;
  const set = (code: string, patch: Partial<{ value: string; rule: string }>) =>
    setDraft({
      ...current,
      [code]: { value: "", rule: "optional", ...current[code], ...patch },
    });

  const save = () =>
    run(
      () =>
        put(
          path,
          Object.entries(current)
            .filter(([, v]) => v.value || v.rule !== "optional")
            .map(([dimensionCode, v]) => ({ dimensionCode, valueCode: v.value, rule: v.rule })),
        ),
      "Dimensions saved",
    ).then(() => setDraft(null));

  return (
    <div className="stack">
      <p className="sub" style={{ margin: 0 }}>
        Written down once here, carried by every document that names this record.
      </p>
      {/* A grid, not a table: in a modal the value dropdown collapsed to a sliver because the
          rule column's explanation took the width. Each part gets a floor, and they stack when
          there is not enough room for three. */}
      <ul className="dim-defaults">
        <li className="dim-head">
          <span>Dimension</span>
          <span>Value</span>
          <span>When a document is made</span>
        </li>
        {dims.map((d) => (
          <li key={d.id}>
            <span className="dim-axis">
              <span className="perm-tag">{d.code}</span> <span className="muted sm">{d.name}</span>
            </span>
            <Select
              value={current[d.code]?.value ?? ""}
              placeholder="—"
              disabled={!canWrite}
              onChange={(e) => set(d.code, { value: e.target.value })}
            >
              <option value="">—</option>
              {d.values
                .filter((v) => v.isActive)
                .map((v) => (
                  <option key={v.id} value={v.code}>
                    {v.code} · {v.name}
                  </option>
                ))}
            </Select>
            <span className="dim-rule">
              <Select
                value={current[d.code]?.rule ?? "optional"}
                disabled={!canWrite}
                onChange={(e) => set(d.code, { rule: e.target.value })}
              >
                {RULES.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </Select>
              <small className="muted">
                {RULES.find(([v]) => v === (current[d.code]?.rule ?? "optional"))?.[2]}
              </small>
            </span>
          </li>
        ))}
      </ul>
      {canWrite && (
        <div className="actions">
          {dirty && (
            <button type="button" className="btn" onClick={() => setDraft(null)} disabled={busy}>
              Cancel
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty}>
            <Icon name="check" size={14} />
            Save dimensions
          </button>
        </div>
      )}
    </div>
  );
}
